import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import LockedPhotoCard from "@/components/LockedPhotoCard";
import UnlockPaymentModal from "@/components/UnlockPaymentModal";
import { Star, Check, ArrowLeft, Images, Play, LogIn, Lock, Share2 } from "lucide-react";
import { euro, initials } from "@/lib/format";
import { toast } from "sonner";
import { prepareExternal, openExternal, cancelExternal } from "@/lib/external";

const CreatorPublic = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [follow, setFollow] = useState(null);

  const toggleFollow = async () => {
    if (!user) return navigate("/auth");
    try {
      const { data } = await api.post(`/users/${encodeURIComponent(username)}/follow`);
      setFollow((f) => ({ ...(f || {}), is_following: data.following, counts: data.counts }));
      toast.success(data.following ? "Abonné" : "Désabonné");
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      if (user) {
        // Authenticated view: get proper purchased/subscribed status
        const [info, wall] = await Promise.all([
          api.get(`/creators/${encodeURIComponent(username)}`),
          api.get(`/creators/${encodeURIComponent(username)}/wall`),
        ]);
        setData(info.data);
        setPosts(wall.data.posts);
        api.get(`/users/${encodeURIComponent(username)}/profile`).then(({ data }) => setFollow(data)).catch(() => {});
      } else {
        const { data } = await api.get(`/public/creators/${encodeURIComponent(username)}`);
        setData(data);
        setPosts(data.posts || []);
      }
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [username, user]);

  useEffect(() => { load(); }, [load]);

  const requireLogin = () => {
    toast.info("Connectez-vous pour continuer");
    navigate("/auth");
  };

  const shareVitrine = async () => {
    const url = `${window.location.origin}/c/${username}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `MIHOD PAY — @${username}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Lien copié !");
      }
    } catch (e) {
      try { await navigator.clipboard.writeText(url); toast.success("Lien copié !"); } catch (_) {}
    }
  };

  const subscribe = async (plan) => {
    if (!user) return requireLogin();
    const reserved = prepareExternal();
    try {
      const { data } = await api.post(
        `/creators/${encodeURIComponent(username)}/subscribe?plan=${plan}`,
        { origin_url: window.location.origin }
      );
      if (openExternal(data.checkout_url, reserved) === "tab") toast.info("Le paiement Stripe s'est ouvert dans un nouvel onglet.");
    } catch (e) {
      cancelExternal(reserved);
      toast.error(apiError(e));
    }
  };

  const openUnlock = (photo) => {
    if (!user) return requireLogin();
    setSelected(photo);
    setModalOpen(true);
    api.post(`/photos/${photo.id}/view`).catch(() => {});
  };

  const confirmUnlock = async (photo) => {
    try {
      const { data } = await api.post(`/photos/${photo.id}/purchase`);
      setUser(data.user);
      toast.success(t("common.unlocked"));
      await load();
    } catch (e) {
      toast.error(apiError(e));
      throw e;
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen grid place-items-center px-6 text-center">
        <div>
          <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary grid place-items-center mb-3"><Lock className="h-6 w-6 text-muted-foreground" /></div>
          <p className="font-display text-xl">{t("wall.notFound")}</p>
          <Button className="mt-4 rounded-xl" onClick={() => navigate(user ? "/" : "/auth")}>{t("auth.backToLogin")}</Button>
        </div>
      </div>
    );
  }

  const creator = data?.creator;
  const counts = data?.counts || { photos: 0, videos: 0, total: 0 };
  const isSubscribed = data?.is_subscribed;

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-card/80 backdrop-blur border-b border-border">
        <div className="max-w-md lg:max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => navigate(user ? "/" : "/auth")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="creator-public-back">
            <ArrowLeft className="h-4 w-4" /> {user ? t("nav.home") : t("auth.login")}
          </button>
          <img src="/photopay-logo.jpeg" alt="MIHOD PAY" className="h-8 w-auto rounded" />
          <div className="flex items-center gap-1">
            <button onClick={shareVitrine} className="h-9 w-9 grid place-items-center rounded-xl hover:bg-secondary transition-colors" data-testid="creator-public-share" aria-label="Partager">
              <Share2 className="h-5 w-5" />
            </button>
            {!user && (
              <Button size="sm" variant="outline" className="rounded-xl" onClick={() => navigate("/auth")} data-testid="creator-public-login">
                <LogIn className="h-4 w-4 mr-1" /> {t("auth.login")}
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-md lg:max-w-3xl mx-auto px-4 py-5 space-y-5">
        {loading ? (
          <>
            <Skeleton className="h-28 rounded-2xl" />
            <div className="grid grid-cols-2 gap-3">
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="aspect-[4/5] rounded-2xl" />)}
            </div>
          </>
        ) : (
          <>
            {/* Creator header */}
            <Card className="p-5 rounded-2xl" data-testid="creator-public-header">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  {creator?.avatar && <AvatarImage src={creator.avatar} alt={creator.username} />}
                  <AvatarFallback className="bg-accent text-accent-foreground text-lg font-semibold">{initials(creator?.username)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-xl truncate">@{creator?.username}</p>
                  <p className="text-xs text-muted-foreground">{data?.subscriber_count ?? 0} {t("profile.subscribersCount").toLowerCase()}</p>
                </div>
                {user && !follow?.is_me && (
                  <Button
                    size="sm"
                    variant={follow?.is_following ? "outline" : "default"}
                    className="rounded-xl shrink-0"
                    onClick={toggleFollow}
                    data-testid="profile-follow-button"
                  >
                    {follow?.is_following ? "Abonné" : "S'abonner"}
                  </Button>
                )}
              </div>

              {/* Follow stats: Publications | Abonnés | Abonnements */}
              {follow?.counts && (
                <div className="grid grid-cols-3 gap-2 mt-4" data-testid="profile-follow-stats">
                  <div className="text-center"><p className="font-display text-lg">{follow.counts.posts}</p><p className="text-[11px] text-muted-foreground">Publications</p></div>
                  <div className="text-center"><p className="font-display text-lg">{follow.counts.followers}</p><p className="text-[11px] text-muted-foreground">Abonnés</p></div>
                  <div className="text-center"><p className="font-display text-lg">{follow.counts.following}</p><p className="text-[11px] text-muted-foreground">Abonnements</p></div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2 mt-4">
                <div className="rounded-xl bg-secondary p-2 text-center">
                  <p className="text-[11px] text-muted-foreground flex items-center justify-center gap-1"><Images className="h-3 w-3" /> {t("profile.photosPublished")}</p>
                  <p className="font-display text-base" data-testid="creator-photo-count">{counts.photos}</p>
                </div>
                <div className="rounded-xl bg-secondary p-2 text-center">
                  <p className="text-[11px] text-muted-foreground flex items-center justify-center gap-1"><Play className="h-3 w-3" /> {t("profile.videosPublished")}</p>
                  <p className="font-display text-base" data-testid="creator-video-count">{counts.videos}</p>
                </div>
                <div className="rounded-xl bg-secondary p-2 text-center">
                  <p className="text-[11px] text-muted-foreground">{t("wall.posts")}</p>
                  <p className="font-display text-base">{counts.total}</p>
                </div>
              </div>

              {data?.sub_enabled && (
                <div className="mt-4">
                  {isSubscribed ? (
                    <Badge className="bg-[hsla(214,100%,62%,0.15)] text-primary border-0 gap-1 w-full justify-center py-2"><Check className="h-4 w-4" /> {t("common.subscribed")}</Badge>
                  ) : (
                    <Button className="w-full rounded-xl neon-glow-blue-sm" onClick={() => setSubOpen(true)} data-testid="creator-public-subscribe">
                      <Star className="h-4 w-4 mr-1" /> {t("common.subscribe")}
                    </Button>
                  )}
                </div>
              )}
            </Card>

            {/* Wall feed */}
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg">{t("wall.by")} @{creator?.username}</h2>
              <span className="text-xs text-muted-foreground">{counts.total} {t("wall.posts")}</span>
            </div>

            {posts.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary grid place-items-center mb-3"><Images className="h-6 w-6" /></div>
                <p className="font-medium text-foreground">{t("wall.empty")}</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3" data-testid="creator-public-feed">
                {posts.map((p) => (
                  <LockedPhotoCard key={p.id} photo={p} onUnlock={null} footer={false} allowSave />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Subscription tiers */}
      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Star className="h-5 w-5 text-primary" /> {t("common.subscribe")} @{creator?.username}</DialogTitle>
            <DialogDescription>2 mois d'essai gratuit, puis renouvellement automatique. Accès à toutes ses publications.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(data?.tiers || []).map((tier) => (
              <button
                key={tier.lookup_key}
                onClick={() => subscribe(tier.lookup_key)}
                data-testid={`public-sub-tier-${tier.lookup_key}`}
                className="w-full flex items-center justify-between rounded-xl border border-border hover:border-primary bg-card px-4 py-3 transition-colors"
              >
                <span className="font-medium">{tier.label}</span>
                <span className="text-primary font-semibold">{euro(tier.price)}</span>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground text-center">Paiement sécurisé par carte via Stripe · essai gratuit 60 jours</p>
        </DialogContent>
      </Dialog>

      <UnlockPaymentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        photo={selected}
        balance={user?.wallet_balance || 0}
        onConfirm={confirmUnlock}
        onRecharge={() => navigate("/portefeuille")}
      />
    </div>
  );
};

export default CreatorPublic;
